/**
 * config.ts - Game Balance Configuration
 *
 * All tunable constants in one place.
 * Rules: No logic, only constants. No functions.
 */

export const CONFIG = {
  // === Tick / Game Loop ===
  tickIntervalMs: 100,
  visitorMoveIntervalMs: 1500,  // ms per room move
  visitorSpawnIntervalMs: 8000, // ms between groups

  // === Building Costs (SP) ===
  floorCosts: [0, 0, 1000, 5000, 25000, 125000] as const,
  // roomSlotCosts[floorLevel] = cost to unlock next slot on that floor
  roomSlotUnlockCost: [0, 200, 1000, 5000, 25000, 125000] as const,

  // === Room Upgrade Costs (fraction of floor cost) ===
  roomUpgrade2Fraction: 0.2,
  roomUpgrade3Fraction: 0.5,

  // === Room Upgrade Multipliers ===
  roomUpgradeMultipliers: [1.0, 1.5, 2.0] as const,

  // === Ghost Hire Costs (SP) ===
  ghostHireCost: {
    C: 100,
    B: 500,
    A: 2000,
    S: 10000,
  } as const,

  // === Ghost Leveling ===
  ghostMaxLevel: 20,
  ghostExpPerScare: 10,
  ghostLevelUpExpBase: 100,      // exp needed for level 2
  ghostLevelUpExpGrowth: 1.5,    // multiplier per level
  ghostLevelBonusPerLevel: 0.05, // +5% scare per level above 1

  // === Fatigue ===
  fatiguePerScare: 5,
  fatigueRecoveryPerHour: 20,
  fatiguePenaltyThreshold: 100,
  fatiguePenaltyMultiplier: 0.5,
  fatigueRecoveryCostSp: 20,    // instant recovery (no ad)

  // === Chain Scare ===
  // chainScareBonus[roomsInARow] = bonus multiplier (0 = no bonus)
  chainScareBonus: [0, 0, 0, 0.2, 0.3, 0.5] as const,
  terrorMaxBonus: 1.0, // x2 when full chain

  // === Room-Ghost Affinity ===
  affinityPerfect: 2.0,
  affinityGood: 1.5,
  affinityNone: 1.0,
  affinityBad: 0.7,

  // === Visitor Spawn ===
  initialGroupSize: 2,
  groupSizeByFloors: [2, 2, 3, 4, 5, 6] as const, // indexed by unlocked floors

  // === Fame ===
  famePerInfluencer: 5,
  famePerVipSuccess: 20,
  famePerVipFail: -10,
  famePerFullRun: 1,

  // === Prestige Requirements ===
  prestigeMinFloors: 5,
  prestigeMinFame: 500,
  prestigeMinTotalSp: 50000,

  // === Prestige Bonuses (per level) ===
  prestigeGhostBonusPerLevel: 0.1,
  prestigeStartSpPerLevel: 1000,
  prestigeGroupSizeBonusPerLevel: 2,

  // === Offline Earnings ===
  offlineEarningsMultiplier: 0.3,
  maxOfflineHours: 8,

  // === Initial State ===
  initialSp: 0,
  initialFame: 0,
  initialPrestigeCoins: 0,
  initialFloors: 1,           // start with 1 floor
  initialRoomSlots: 1,        // floor 1 starts with 1 slot open
} as const;

export type GameConfig = typeof CONFIG;
